import { useCallback, useEffect, useState } from "react";
import { createSessionClient } from "../api/client";
import { MemberProfile } from "../types";
import Button from "./ui/Button";

const NOISE = [
  { value: "low", label: "Low-key" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "Lively" },
];

/**
 * A magic-link member edits their own persistent preferences (dietary/allergies,
 * noise tolerance, things to avoid). Carries across plans; softly steers options
 * and flags conflicts. Privacy-sensitive items are member-controlled.
 */
const MemberPreferences: React.FC<{ token: string }> = ({ token }) => {
  const [dietary, setDietary] = useState("");
  const [noise, setNoise] = useState<string | null>(null);
  const [avoid, setAvoid] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const { data } = await createSessionClient(token).get<MemberProfile>("/api/session/profile");
    setDietary(data.dietary ?? "");
    setNoise(data.noiseTolerance ?? null);
    setAvoid((data.avoid ?? []).join(", "));
    setLoaded(true);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await createSessionClient(token).put("/api/session/profile", {
        dietary: dietary.trim() || null,
        noiseTolerance: noise,
        avoid: avoid
          .split(",")
          .map((a) => a.trim())
          .filter(Boolean),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="mt-4 rounded-2xl border border-primary-100 bg-white/70 p-4 text-left">
      <summary className="cursor-pointer text-sm font-medium text-ink">Your preferences</summary>
      <p className="mt-1 text-xs text-ink/55">
        Private to you. Helps Kinoes suggest things you'll like and flag ones to skip — across all your plans.
      </p>
      {!loaded ? (
        <p className="mt-3 text-sm text-ink/40">Loading…</p>
      ) : (
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-xs font-medium text-ink/70">Dietary / allergies</label>
            <input
              value={dietary}
              onChange={(e) => setDietary(e.target.value)}
              placeholder="e.g. nut allergy, vegetarian"
              className="mt-1 w-full rounded-xl border border-primary-200 bg-white/70 px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-ink/70">Vibe</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {NOISE.map((n) => (
                <button
                  key={n.value}
                  type="button"
                  onClick={() => setNoise(noise === n.value ? null : n.value)}
                  className={`rounded-xl border px-2 py-1.5 text-sm transition-colors ${
                    noise === n.value
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-primary-200 bg-white/70 text-ink/70 hover:border-primary-300"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-ink/70">Things to avoid</label>
            <input
              value={avoid}
              onChange={(e) => setAvoid(e.target.value)}
              placeholder="comma-separated, e.g. horror, political events"
              className="mt-1 w-full rounded-xl border border-primary-200 bg-white/70 px-3 py-2 text-sm text-ink placeholder:text-ink/40 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : saved ? "Saved ✓" : "Save preferences"}
          </Button>
        </div>
      )}
    </details>
  );
};

export default MemberPreferences;
